var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { EMPTY_TEXT } from '@mitchell/react.component.ux';
import { resolveResponse } from "../../common";
import { IntelligentPhotoCaptureApplicationType, IntelligentPhotoCaptureSeverityType } from '../enums';
/**
 * Builds a normalized error payload by merging resolved response data with HTTP status details.
 * @template T - Response data type.
 * @param {Response} response - Raw fetch response used to capture status metadata.
 * @param {T} data - Parsed response body from resolveResponse.
 * @return {Record<string, unknown>} Consolidated error payload with status fields.
 */
const buildErrorPayload = (response, data) => {
    const resolvedData = typeof data === 'object' && data !== null ? data : { data };
    return Object.assign(Object.assign({}, resolvedData), { StatusCode: response.status });
};
/**
 * Resolves and validates an HTTP response, rejecting with a standardized payload when failed.
 * @template T - Expected resolved response type.
 * @param {Response} response - HTTP response to resolve.
 * @return {Promise<T>} Parsed response data when status is successful.
 */
export const handleResolveOrRejectResponse = (response) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const data = yield resolveResponse(response);
        if (!response.ok) {
            return Promise.reject(buildErrorPayload(response, data));
        }
        return data;
    }
    catch (error) {
        return Promise.reject(buildErrorPayload(response, error));
    }
});
/**
 * Safely converts any value to a JSON string representation.
 * @template T - Input value type.
 * @param {T} value - Value to stringify.
 * @return {string} Stringified content or string fallback when serialization fails.
 */
export const safeJsonStringify = (value) => {
    if (typeof value === 'string') {
        return value;
    }
    try {
        const json = JSON.stringify(value);
        return typeof json === 'string' ? json : String(value);
    }
    catch (_a) {
        return String(value);
    }
};
/**
 * Returns the work item identifier used by error logging.
 * @param {string} [instanceReferenceId] - Optional instance reference from estimator context.
 * @return {string} Instance reference id or EMPTY_TEXT when unavailable.
 */
export const getIntelligentPhotoCaptureWorkItemId = (instanceReferenceId) => instanceReferenceId && instanceReferenceId.trim() ? instanceReferenceId : EMPTY_TEXT;
/**
 * Determines the application type for intelligent photo capture error logging.
 * @param {string} [instanceReferenceId] - Optional instance reference from estimator context.
 * @return {IntelligentPhotoCaptureApplicationType} Estimator application type when instance id exists, otherwise PBE.
 */
export const getIntelligentPhotoCaptureApplicationType = (instanceReferenceId) => instanceReferenceId && instanceReferenceId.trim()
    ? IntelligentPhotoCaptureApplicationType.MIPC_ESTIMATOR
    : IntelligentPhotoCaptureApplicationType.PBE;
/**
 * Builds a standardized failure error log request for intelligent photo capture service calls.
 * @template TPayload - Request payload type.
 * @template TResponse - Response payload type.
 * @param {IIntelligentPhotoCaptureBuildErrorLogRequestParams<TPayload, TResponse>} params - Error log request inputs.
 * @return {IIntelligentPhotoCaptureErrorLogRequest} Structured error log request body.
 */
export const buildIntelligentPhotoCaptureFailureErrorLogRequest = (params) => {
    const { instanceReferenceId, claimNumber, errorDetail, requestedPayload, response, requestedHttpMethod, actionType } = params;
    return Object.assign({ severity: IntelligentPhotoCaptureSeverityType.FATAL, workItemID: getIntelligentPhotoCaptureWorkItemId(instanceReferenceId), errorDetail: errorDetail || EMPTY_TEXT, actionType,
        requestedHttpMethod, requestedPayload: safeJsonStringify(requestedPayload), response: safeJsonStringify(response), applicationType: getIntelligentPhotoCaptureApplicationType(instanceReferenceId) }, (claimNumber && claimNumber.trim() ? { claimNumber } : {}));
};
//# sourceMappingURL=intelligentPhotoCaptureErrorLogUtilities.js.map